import React, { useMemo, useRef, useEffect } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { ViewportList } from 'react-viewport-list';

import { filtersSelector } from 'store/modules/competition-nav/selectors/filters-selector';
import { useGetMatchesQuery } from 'store/modules/api/afl-api';
import { groupMatchesByDate } from '../../utils/group-matches-by-date';
import { groupMatchesByRoundName } from '../../utils/group-matches-by-roundName';
import { filterByCity } from '../../utils/filter-by-city';

import { EmptyState } from 'common/react/components/EmptyState';

import { FixturesItem } from './FixturesItem';
import { ByesGroup } from './ByesGroup';
import { FixturesSkeleton } from './FixturesSkeleton';
import { FixturesLegend } from './FixturesLegend';

import { getTranslation } from 'common/react/utils/translations';

const LAST_BYE_ROUND = 23;
const FLATTEN_DEPTH = 4;
const MARGIN_BOTTOM_DESKTOP = 24;
const MARGIN_BOTTOM_MOBILE = 16;

const checkIfBroadcasterGuide = (providerId) => {
    const translationString = getTranslation(
        `label.broadcastGuideURL.${providerId}`
    );

    if (translationString !== `label.broadcastGuideURL.${providerId}`) {
        return translationString;
    }

    return false;
};

export const FixturesList = ({
    competitionId,
    compSeason,
    round,
    broadcasterPromo,
    scrollToRound,
    setScrollToRound
}) => {
    const filters = useSelector(filtersSelector);
    const selectedTimeZone = useSelector((state) => state.selectedRegion);
    const listRef = useRef(null);

    const disableRounds =
        filters?.cities.length > 0 ||
        filters?.teams.length > 0 ||
        filters?.venues.length > 0;

    const broadcastGuideUrl =
        checkIfBroadcasterGuide(compSeason.providerId) ?? '';

    const { matches = [], isMatchesFetching } = useGetMatchesQuery(
        {
            pageSize: 300,
            competitionId: competitionId,
            compSeasonId: compSeason.id,
            ...(!disableRounds && { roundNumber: round.roundNumber }),
            ...(filters.teams.length && { teamId: filters.teams }),
            ...(filters.venues.length && { venueId: filters.venues })
        },
        {
            selectFromResult: ({ data, isFetching }) => ({
                matches:
                    data && filters.cities.length
                        ? filterByCity(data, filters.cities)
                        : data,
                isMatchesFetching: isFetching
            })
        }
    );

    /**
     * (1) Groups Fixtures first by their date or a generic TBCPC if they are
     * cancelled/TBC/postponded
     * (2) Then goes through each date in the grouped object and for each day
     * groups the matches by round name and then converts that object into an
     * array, e.g. ['Round 1', {match}, 'Round 2', {match}]
     * (3) Takes the complete object converts it to an array and flattens it
     * completely so we end up with something like this:
     * ["20240314", "Round 1", {match}, "20240315" ... etc]
     */
    const fixtureItems = useMemo(() => {
        // Group results based on start day/month
        if (matches) {
            // (1)
            const groupedByDate = groupMatchesByDate(
                matches,
                selectedTimeZone.timezone
            );

            // (2)
            for (const date in groupedByDate) {
                if (Object.prototype.hasOwnProperty.call(groupedByDate, date)) {
                    const groupedByRoundName = groupMatchesByRoundName(
                        groupedByDate[date]
                    );
                    groupedByDate[date] = Object.entries(groupedByRoundName);
                }
            }

            // (3)
            return Object.entries(groupedByDate).flat(FLATTEN_DEPTH);
        }

        return [];
    }, [matches, selectedTimeZone.timezone]);

    const roundNumber = round.roundNumber;

    const isConferenceSeason = matches.some(
        (match) => match?.home?.conference || match?.away?.conference
    );

    /**
     * If rounds are disabled (so we're showing multiple rounds) we should
     * listen for when the round changes, i.e. a round button is clicked, and
     * scroll the viewport list to that clicked round.
     */
    useEffect(() => {
        if (scrollToRound && disableRounds) {
            const targetIndex = fixtureItems.findIndex(
                (item) => item?.round?.roundNumber === scrollToRound
            );
            if (targetIndex && listRef.current) {
                listRef.current.scrollToIndex({
                    index: targetIndex,
                    offset: -160
                });
            }
            setScrollToRound(null);
        }
    }, [disableRounds, fixtureItems, scrollToRound, setScrollToRound]);

    if (isMatchesFetching) {
        return <FixturesSkeleton />;
    }

    if (!matches || !matches.length) {
        return <EmptyState cssClass="competition-nav__empty-state" />;
    }

    // find first live or pre-match fixture
    let firstLiveOrUpcomingFixture = {};
    firstLiveOrUpcomingFixture = [...matches]
        .reverse()
        .find(
            (match) =>
                PULSE.app.common.match.getMatchStatus(match.status) ===
                PULSE.app.common.CONSTANTS.MATCH_STATUS.LIVE
        );
    if (!firstLiveOrUpcomingFixture) {
        firstLiveOrUpcomingFixture = matches.find(
            (match) =>
                PULSE.app.common.match.getMatchStatus(match.status) ===
                PULSE.app.common.CONSTANTS.MATCH_STATUS.UPCOMING
        );
    }

    return (
        <>
            {fixtureItems?.length && disableRounds ? (
                <ViewportList
                    ref={listRef}
                    items={fixtureItems}
                    itemMargin={
                        window.matchMedia('(max-width: 640px)').matches
                            ? MARGIN_BOTTOM_MOBILE
                            : MARGIN_BOTTOM_DESKTOP
                    }
                    overscan={10}
                    renderSpacer={({ ref, style }) => (
                        <div ref={ref} style={style}>
                            <FixturesSkeleton></FixturesSkeleton>
                        </div>
                    )}
                >
                    {(item, index) => (
                        <FixturesItem
                            key={
                                typeof item === 'string'
                                    ? `${item}-${index}`
                                    : item.id
                            }
                            item={item}
                            broadcastGuideUrl={broadcastGuideUrl}
                            disableRounds={disableRounds}
                            broadcasterPromo={broadcasterPromo}
                            firstLiveOrUpcomingFixture={
                                firstLiveOrUpcomingFixture?.id
                            }
                        />
                    )}
                </ViewportList>
            ) : fixtureItems?.length ? (
                fixtureItems.map((item, index) => (
                    <FixturesItem
                        key={
                            typeof item === 'string'
                                ? `${item}-${index}`
                                : item.id
                        }
                        item={item}
                        broadcastGuideUrl={broadcastGuideUrl}
                        disableRounds={disableRounds}
                        broadcasterPromo={broadcasterPromo}
                        firstLiveOrUpcomingFixture={
                            firstLiveOrUpcomingFixture?.id
                        }
                    />
                ))
            ) : null}

            {!isNaN(roundNumber) &&
            roundNumber < LAST_BYE_ROUND &&
            !disableRounds &&
            compSeason.id ? (
                <ByesGroup
                    compseasonId={compSeason.id}
                    roundNumber={roundNumber}
                />
            ) : null}

            {isConferenceSeason && <FixturesLegend />}
        </>
    );
};

FixturesList.propTypes = {
    competitionId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    compSeason: PropTypes.object,
    round: PropTypes.object,
    broadcasterPromo: PropTypes.object,
    scrollToRound: PropTypes.number,
    setScrollToRound: PropTypes.func
};
